import { defineComponent as _defineComponent } from 'vue'
import { PropType, ref, Ref, computed, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { Carousel, Slide, Pagination } from '@/lib/vue3-carousel/src'
import CarouselTrigger from '@/components/task/CarouselTrigger.vue'
import { playSounds, stopSounds } from '@/utilities'
import { useCMSStore, CMSStoreType } from '@/templates/slplus/store/useCMSStore'
import { useDeviceService } from '@/composition/useDevice'
import { useUserStore } from '@/store/useUserStore'
import { useGameStore } from '@/store/useGameStore'
import { Day } from '@/templates/slplus/structure/generalModels'
import { Enig, Thumbs, TASK_TYPES } from '@/templates/slplus/structure/questionModels'
import { TRACKING_TYPE, TrackingDetails } from '@/types/main'
import Button from '@/components/base/Button.vue'
import RecordButton from '@/components/base/RecordButton.vue'

import Intro from '@/components/task/Intro.vue'
import Instruction from '@/components/task/Instruction.vue'
import VoiceRecording from '@/components/task/enig/VoiceRecording.vue'
import DiscussionTime from '@/components/task/enig/DiscussionTime.vue'
import FindPlaces from '@/components/task/enig/FindPlaces.vue'
import Quizz4Way from '@/components/task/enig/Quizz4Way.vue'
import QuizzResults from '@/components/task/enig/QuizzResults.vue'

import omarWithGlassesImage from '@/assets/tasks/enig/omarWithGlasses.svg'
import omarWithGlassesCorrect from '@/assets/tasks/enig/omarWithGlassesCorrect.svg'
import omarWithGlassesWrong from '@/assets/tasks/enig/omarWithGlassesWrong.svg'
import enigImage2 from '@/assets/tasks/enig/enig2.svg'

import trudeluttSound from '@/assets/tasks/stovnerfaret_26_10.mp3'
import introSound from '@/assets/tasks/enig/EMO_intro.mp3'
import rightSound from '@/assets/tasks/enig/EMO_rett.mp3'
import wrongSound from '@/assets/tasks/enig/EMO_feil1ab.mp3'
//import instruksBilde from '@/assets/tasks/enig/EMO_instruksBILDE.mp3'
import instruksFilm from '@/assets/tasks/enig/EMO_instruksFILM.mp3'
import instruks2Sound from '@/assets/tasks/enig/EMO_tusentakk.mp3'
import hor1Sound from '@/assets/tasks/enig/Enig_med_Omar_hor1.mp3'
import hor2Sound from '@/assets/tasks/enig/Enig_med_Omar_hor2.mp3'
import hor3Sound from '@/assets/tasks/enig/Enig_med_Omar_hor3.mp3'
import avslutSound from '@/assets/tasks/enig/Enig_med_Omar_avslut.mp3'
import settDeg from '@/assets/tasks/horerduordet/HDO_intro3.mp3'

enum MODES {
  intro = 'intro',
  firstInstruction = 'firstInstruction',
  movie = 'movie',
  discussion = 'discussion',
  recording = 'recording',
  secondInstruction = 'secondInstruction',
  quizzInstruction = 'quizzInstruction',
  places = 'places',
  playQuizz = 'playQuizz',
  quizzResults = 'quizzResults',
  endRound = 'endRound',
  finalDiscussion = 'finalDiscussion',
  finished = 'finished',
}
type ImageKey = 0 | 1 | 2 | 3
type Results = {
  [key in ImageKey]: { answer: Thumbs; correct: boolean } | undefined
}
interface MeningResults {
  [key: string]: Results
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Enig',
  props: {
  question: { required: true, type: Object as PropType<Enig> },
  myIndex: { required: false, type: Number, default: 0 },
},
  emits: ['start-introduction', 'completed', 'not-completed'],
  setup(__props, { expose, emit }) {
  expose();

const props = __props

const messages = {
  no: {
    title: 'Snakk med Omar',
    description: 'Hjelp Omar med å lære ord!',
    getStarted: 'Start',
    exercise: 'Aktivitet ',
    yourTurn: 'Er dere klare?',
    weReady: 'Continue',
    letsDiscuss: 'La oss alle snakke om det!',
    titleFinished: 'Takk for hjelpen!',
    descriptionFinished: '',
    replay: 'Spill igjen',
    omarThinks: 'Omar tror',
    seeAgain: 'Se igjen',
  },
  en: {
    title: 'Speaking with Omar',
    description: 'Help Omar to learn words!',
    getStarted: 'get started',
    exercise: 'Exercise ',
    yourTurn: 'Are you ready?',
    weReady: 'Continue',
    letsDiscuss: "Let's all talk about it!",
    titleFinished: 'Thanks for your help!',
    descriptionFinished: '',
    replay: 'Play again',
    omarThinks: 'Omar thinks',
    seeAgain: 'Watch again',
  },
}

// These modes should appear in order to allow 'back' button to work



const { t } = useI18n({ messages })
const { question } = toRefs(props)
const mode: Ref<MODES> = ref(MODES.intro)
const cmsStore: CMSStoreType = useCMSStore()
const gameStore = useGameStore()
const { getters: deviceGetters, actions: deviceActions } = useDeviceService()
const userStore = useUserStore()
const selectedSet: Day = cmsStore.getters.selectedSet.value.set as Day
const word = selectedSet ? selectedSet.word : ''
const players = gameStore.getters.playersInSelectedGame
const results: Ref<MeningResults> = ref({})
const videoPlayer = ref()
const videoIsPlaying = ref(false)
const meningRound = ref(0)
let placesSeen = false
let savedState: Ref<MODES | undefined> = ref()
let currentTracking: TrackingDetails | undefined

const instructionPages = ref([
  {
    image: omarWithGlassesCorrect,
    imageHeight: '350px',
    audio: rightSound,
    speakerTop: '7rem',
    speakerRight: '15rem',
  },
  {
    image: omarWithGlassesWrong,
    imageHeight: '22rem',
    audio: wrongSound,
    speakerTop: '7rem',
    speakerRight: '-1rem',
  },
])

// Initial setup
/* if (question.value) {
      // Create a shuffled list of items to display
      // items.value = shuffleItems(question.value.items)
      // Configure the parent with this Question's introduction audio
      context.emit('setup-audio-introduction', '/assets/sounds/tasks/sevenseas/intro.mp3')
    } */

const exerciseIndex = computed(() => `${t('exercise')} ${props.myIndex + 1}`)

// Play the introduction audio automatically
emit('start-introduction', true)

// Configure the output object at any time. It will be included when calling 'completed'

// Call this when the task is complete
const finish = async () => {
  stopSounds()
  if (mode.value === MODES.movie) {
    videoPlayer.value.pause()
    return changeMode(MODES.discussion)
  }
  finishAudioTracking()
  // Call this when the task is complete
  if (mode.value === MODES.finished) emit('completed')
  // Call when finishing early
  else emit('not-completed')
  currentTracking = undefined
}

// Review a state whilst allowing return to the current state
const reviewMode = (m: MODES): void => {
  savedState.value = mode.value
  changeMode(m, true)
}

const resultsReady = (r: Results) => {
  results.value[meningRound.value] = r
  mode.value = MODES.quizzResults
}

// -------------  Enig -----------------

const beginAudioTracking = (name: string) => {
  const game = gameStore.getters.selectedGame.value
  if (game) {
    currentTracking = new TrackingDetails({
      itemID: props.question._id,
      gameID: game._id,
      playerIDs: players.value.map((p) => p._id),
      projectID: game.project.id,
      type: TRACKING_TYPE.subtask,
      details: {
        user: userStore.getters.myUser.value.profile.username,
        results: results.value,
        taskType: TASK_TYPES.enig,
        taskInfo: `${question.value.name}-${name}`,
        word,
        isCorrect: true,
      },
    })
  }
  if (!deviceGetters.recordingNow.value) {
    deviceActions.createAudio().then(() => {
      deviceActions.startRecordingAudio()
    })
  }
}
const finishAudioTracking = async () => {
  await deviceActions.stopRecordingAudio()
  if (currentTracking) {
    currentTracking.audioFile = deviceGetters.audioFilename.value
    currentTracking.complete()
    gameStore.actions.commitNewTracking(currentTracking)
  }
}

const playFirstInstruction = () => {
  stopSounds()
  playSounds([instruksFilm])
}

const playSecondInstruction = () => {
  stopSounds()
  playSounds([instruks2Sound])
}

const back = () => {
  const modes: MODES[] = Object.values(MODES)
  const previousModeIndex = modes.indexOf(mode.value) - 1
  if (previousModeIndex > -1) changeMode(modes[previousModeIndex])
}

const changeMode = (newMode: MODES, reviewMode?: boolean): void => {
  stopSounds()

  // This section returns to a state after reviewing video, placement or instructions
  if (savedState.value && !reviewMode) {
    const m = savedState.value
    savedState.value = undefined
    return changeMode(m)
  }

  let changedMode = newMode
  switch (newMode) {
    case MODES.intro:
      playSounds([trudeluttSound, introSound])
      break
    case MODES.firstInstruction:
      playSounds([instruksFilm])
      break
    case MODES.movie:
      break
    case MODES.discussion:
      beginAudioTracking('discussion')
      break
    case MODES.recording:
      finishAudioTracking()
      break
    case MODES.secondInstruction:
      playSounds([instruks2Sound])
      break
    case MODES.quizzInstruction:
      if (localStorage.getItem('instSeen-enig') === 'true' && !reviewMode) changedMode = MODES.playQuizz
      else playSounds([rightSound])
      break
    case MODES.places:
      if (placesSeen && !reviewMode) changedMode = MODES.playQuizz
      else {
        playPlacesSound()
        placesSeen = true
      }
      break
    case MODES.finalDiscussion:
      break
    case MODES.playQuizz:
      localStorage.setItem('instSeen-enig', 'true')
      playOmarMening()
      beginAudioTracking('quizz')
      break
    case MODES.endRound:
      if (meningRound.value < question.value.mening.length - 1) {
        meningRound.value++
        changedMode = MODES.playQuizz
      } else {
        changedMode = MODES.finished
        playSounds([avslutSound])
      }
      break
    case MODES.finished:
      break
  }
  changedMode !== newMode ? changeMode(changedMode) : (mode.value = newMode)
}

const playVideo = () => {
  videoPlayer.value.addEventListener('ended', () => {
    setTimeout(() => {
      videoIsPlaying.value = false
      changeMode(MODES.discussion)
    }, 1000)
  })
  videoPlayer.value.play()
  videoIsPlaying.value = true
}
// Handles video pause event
const pauseVideo = () => {
  videoPlayer.value.pause()
  videoIsPlaying.value = false
}
const togglePlayPause = () => {
  videoIsPlaying.value ? pauseVideo() : playVideo()
}

const playPlacesSound = () => {
  playSounds([settDeg])
}
const playOmarMening = () => {
  const horSound = meningRound.value === 0 ? hor1Sound : meningRound.value === 1 ? hor2Sound : hor3Sound
  const omarSpeaks = question.value.mening[meningRound.value] ? question.value.mening[meningRound.value].omarStatementAudio : ''
  playSounds([horSound, omarSpeaks])
}

changeMode(MODES.intro)

const __returned__ = { messages, MODES, emit, props, t, question, mode, cmsStore, gameStore, deviceGetters, deviceActions, userStore, selectedSet, word, players, results, videoPlayer, videoIsPlaying, meningRound, placesSeen, savedState, currentTracking, instructionPages, exerciseIndex, finish, reviewMode, resultsReady, beginAudioTracking, finishAudioTracking, playFirstInstruction, playSecondInstruction, back, changeMode, playVideo, pauseVideo, togglePlayPause, playPlacesSound, playOmarMening, Carousel, Slide, Pagination, CarouselTrigger, playSounds, stopSounds, Button, RecordButton, Intro, Instruction, VoiceRecording, DiscussionTime, FindPlaces, Quizz4Way, QuizzResults, omarWithGlassesImage, enigImage2 }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})